import { useQuery } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { EventEligibilityVerdict } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Per-event eligibility verdicts for the signed-in player (EPIC 1.2 multi-event
 * registration). GET /tournaments/{id}/eligibility → EventEligibilityVerdict[] —
 * one verdict per event ({ event_id, event_code?, eligible, reasons[] }) in the
 * backend's deterministic order.
 *
 * Eligibility logic is BACKEND-owned (gender / age / tier / EBC caps). The hook
 * returns the server list VERBATIM and the consuming row renders reasons[0].message
 * as-is — the FE NEVER recomputes eligibility. AUTH-gated and SERVER-SCOPED to the
 * token user (the route resolves the caller from the token; no uid is threaded),
 * so an anonymous viewer never triggers it (the public detail must not 401). The
 * call fires ONLY when there is a logged-in user AND a tournamentId.
 *
 * Its OWN cache slot + query (singular "tournament" prefix) so the per-event
 * eligibility read is isolated like Events/Draws — a failing eligibility fetch
 * never blanks the main detail; the row degrades to "actionable, no greyed
 * reason" rather than blocking the page.
 */
export function useTournamentEligibility(tournamentId: string | undefined) {
  const { user } = useAuth();
  return useQuery<EventEligibilityVerdict[]>({
    queryKey: queryKeys.tournaments.eligibility(tournamentId ?? ""),
    queryFn: () =>
      tournamentApi.getTournamentEligibility(tournamentId as string),
    enabled: Boolean(user && tournamentId),
  });
}
